
import React, { useState, useEffect, useRef } from 'react';
import Vapi from '@vapi-ai/web';
import 'animate.css';

// Safely render any transcript or message value
function renderSafeText(val) {
  if (val == null) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number') return String(val);
  if (Array.isArray(val)) return val.map(renderSafeText).join(' ');
  if (typeof val === 'object') {
    // Handle specific object structures
    if (val.msg) return String(val.msg);
    if (val.details) return String(val.details);
    if (val.type && typeof val.type === 'string') return val.type;
    if (val.transcript) return renderSafeText(val.transcript);
    if (val.errorMsg) return String(val.errorMsg);
    if (val.error) return String(val.error);
    if (val.action) return String(val.action);
    if (val.callClientId) return String(val.callClientId);
    // Handle the new object structure with type, msg, details
    if (val.type && val.msg && val.details) {
      return `${val.type}: ${val.msg} - ${val.details}`;
    }
    // For any other object, return a safe string representation
    return '[Object]';
  }
  return String(val);
}

// Extra safe wrapper to prevent any object rendering
function safeRender(val) {
  try {
    const result = renderSafeText(val);
    // Double check that we never return an object
    if (typeof result === 'object') {
      return '[Object]';
    }
    return result;
  } catch (err) {
    console.error('Error in safeRender:', err);
    return '[Error]';
  }
}

function App() {
  const [appointmentSuccess, setAppointmentSuccess] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isRinging, setIsRinging] = useState(false);
  const [transcript, setTranscript] = useState([]);
  const [finalMessages, setFinalMessages] = useState([]);
  const [error, setError] = useState(null);
  const [isCreatingContact, setIsCreatingContact] = useState(false);
  const [contactId, setContactId] = useState(null);
  const vapiInstance = useRef();
  const dialAudioRef = useRef();
  const [form, setForm] = useState({
    firstname: '',
    lastname: '',
    company: '',
    email: '',
    timezone: 'Asia/Shanghai',
  });
  const chatboxRef = useRef();
  const [appointmentModal, setAppointmentModal] = useState(false);
  const [appointment, setAppointment] = useState({
    date: '',
    time: '',
    firstname: '',
    lastname: '',
    company: '',
    email: '',
    timezone: form.timezone || '',
    areaCode: '+63',
    number: ''
  });
    const [appointmentLoading, setAppointmentLoading] = useState(false);

  useEffect(() => {
    window.openAiModal = () => {
      setModalVisible(true);
    };
    // Use hardcoded API key for development, meta tag for production
    function getVapiApiKey() {
      if (process.env.NODE_ENV === 'development') {
        return process.env.REACT_APP_VAPI_API_KEY || '';
      }
      const meta = document.querySelector('meta[name="vapi-api-key"]');
      return meta ? meta.getAttribute('content') : '';
    }
    const vapi = new Vapi(getVapiApiKey());
    vapiInstance.current = vapi;
    vapi.on('call-start', () => {
      setIsConnected(true);
      setIsRinging(false);
      setIsCreatingContact(false);
      if (dialAudioRef.current) {
        dialAudioRef.current.pause();
        dialAudioRef.current.currentTime = 0;
      }
    });
    vapi.on('call-end', () => {
      setIsConnected(false);
      setIsRinging(false);
      setIsCreatingContact(false);
    });
    vapi.on('message', (msg) => {
      // Ensure we only add valid message objects to transcript
      if (msg && typeof msg === 'object' && msg !== null) {
        // Additional safety: ensure msg has expected structure
        const safeMsg = {
          ...msg,
          transcript: msg.transcript ? safeRender(msg.transcript) : '',
          role: msg.role || 'unknown',
          transcriptType: msg.transcriptType || 'unknown'
        };
        
        setTranscript((prev) => [...prev, safeMsg]);
        // Detect trigger phrase in assistant transcript
        if (
          safeMsg.role === 'assistant' &&
          safeMsg.transcriptType === 'final' &&
          safeMsg.transcript &&
          (
            safeMsg.transcript.toLowerCase().includes('appointment-form') ||
            safeMsg.transcript.toLowerCase().includes('appointment window') ||
            safeMsg.transcript.toLowerCase().includes('appointment form') ||
            safeMsg.transcript.toLowerCase().includes('appointment form window')
          )
        ) {
          setAppointmentModal(true);
        }
        // Update chatbox transcript for final messages
        if (safeMsg.transcriptType === 'final') {
          setFinalMessages((prev) => [...prev, safeMsg]);
        }
      }
    });
    vapi.on('error', (err) => {
      setError(err?.message || err?.error || JSON.stringify(err) || 'Unknown error');
      setIsRinging(false);
      if (dialAudioRef.current) {
        dialAudioRef.current.pause();
        dialAudioRef.current.currentTime = 0;
      }
    });
    return () => vapiInstance.current?.stop();
  }, []);

  // Submit contact data to N8N webhook and get contact_id
  const collectContactData = async (contactData) => {
    try {
      const response = await fetch('https://softdevilo.app.n8n.cloud/webhook/log-a-call', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstname: contactData.firstname,
          lastname: contactData.lastname,
          email: contactData.email,
          company: contactData.company,
          timezone: contactData.timezone,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const contactId = result.contact_id;
      
      if (!contactId) {
        throw new Error('No contact_id received from webhook');
      }

      console.log('Contact data submitted successfully:', result);
      return contactId;
    } catch (err) {
      console.error('Failed to submit contact data:', err);
      throw err;
    }
  };

  const handleClose = () => {
    setModalVisible(false);
    setError(null);
  };
  useEffect(() => {
    if (chatboxRef.current) {
      chatboxRef.current.scrollTop = chatboxRef.current.scrollHeight;
    }
  }, [finalMessages]);
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };
  const handleAppointmentChange = (e) => {
    const { name, value } = e.target;
    setAppointment((prev) => ({ ...prev, [name]: value }));
  };
  const handleAppointmentSubmit = (e) => {
    e.preventDefault();
    // Submit appointment data to API
      setAppointmentLoading(true);
      fetch('https://softdevilo.app.n8n.cloud/webhook/post-appointment-time', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          firstname: form.firstname,
          lastname: form.lastname,
          company: form.company,
          email: form.email,
          timezone: form.timezone,
          date: appointment.date,
          time: appointment.time,
          areaCode: appointment.areaCode,
          number: appointment.number,
          contact_id: contactId
        })
      })
        .then(res => res.ok ? res.json() : Promise.reject(res))
        .then(() => {
          setAppointmentSuccess(true);
          setAppointmentLoading(false);
          setTimeout(() => {
            setAppointmentModal(false);
            setAppointment({
              date: '',
              time: '',
              firstname: '',
              lastname: '',
              company: '',
              email: '',
              timezone: form.timezone || '',
              areaCode: '+63',
              number: ''
            });
            setAppointmentSuccess(false);
          }, 3000);
        })
        .catch(() => {
          setAppointmentLoading(false);
          alert('Failed to submit appointment. Please try again.');
        });
  };
  const startCall = async () => {
    setError(null);
    setModalVisible(false);
    setIsRinging(true);
    setIsCreatingContact(true);
    
    // Clear previous transcript data
    setTranscript([]);
    setFinalMessages([]);
    
    if (dialAudioRef.current) {
      dialAudioRef.current.currentTime = 0;
      dialAudioRef.current.play();
    }

    try {
      // Submit contact data to N8N webhook and get contact_id
      const contactId = await collectContactData(form);
      setContactId(contactId);
      
      // Start Vapi call with contact ID included
      vapiInstance.current?.start('7b5a44f3-2d4b-4bac-924f-6cdc8bc5a6f5', {
        variableValues: {
          first_name: form.firstname,
          last_name: form.lastname,
          email: form.email,
          company: form.company,
          timezone: form.timezone,
          contact_id: contactId,
        }
      });
    } catch (err) {
      console.error('Failed to start call:', err);
      setError(`Failed to start call: ${err?.message || err?.toString() || 'Unknown error'}`);
      setIsRinging(false);
      setIsCreatingContact(false);
      if (dialAudioRef.current) {
        dialAudioRef.current.pause();
        dialAudioRef.current.currentTime = 0;
      }
    }
  };

  return (
    <div className="">
      <audio ref={dialAudioRef} src="https://api-01.callboxinc.com/inbound/dial.mp3" loop style={{ display: 'none' }} />
      <button
        className={
          'talk-button ' + (isConnected
            ? 'on-call animate__animated animate__pulse animate__infinite'
            : isRinging
              ? 'bg-yellow-400 text-black animate__animated animate__headShake animate__infinite'
              : 'bg-yellow-400 text-black hover:bg-yellow-500')
        }
        onClick={() => {
          if (isConnected) {
            vapiInstance.current?.stop();
          } else if (!isRinging) {
            setModalVisible(true);
          }
        }}
        disabled={isRinging}
      >
        {isConnected ? 'End Call' : isRinging ? 'Ringing...' : 'Talk to AI Assistant'}
      </button>


      {/* Conversation chatbox and input smiley */}
      {isConnected && (
        <div className="fixed bottom-8 bg-white p-3 rounded-lg right-8 flex flex-col items-end z-50" style={{maxWidth: '340px'}}>
          {/* Conversation chatbox */}

          <div ref={chatboxRef} className="w-full mb-2 flex flex-col space-y-2 " style={{maxHeight: '320px', overflowY: 'auto'}}>
            {finalMessages.map((msg, idx) => (
              <div key={idx} className={
                `px-4 py-2 rounded-lg text-sm shadow ` +
                (msg.role === 'assistant' ? 'bg-yellow-100 text-yellow-900 self-end' : 'bg-green-100 text-green-900 self-start')
              }>
                {msg && msg.transcript ? safeRender(msg.transcript) : ''}
              </div>
            ))}
          </div>
          {/* Smiley icon as input box */}
          <div className="w-full flex items-center flex-row justify-center gap-2">
            <div className={
              'w-12 h-12 rounded-full flex items-center justify-center text-2xl font-bold shadow-lg mb-1 ' +
              (transcript.length > 0 && transcript[transcript.length-1].role === 'assistant'
                ? 'bg-yellow-400 text-yellow-900'
                : 'bg-green-400 text-green-900')
            }>
              {/* Pure SVG smiley emoji, with conditional mouth-open class */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 48 48"
                width="32" height="32"
                className={
                  (() => {
                    const partial = [...transcript].reverse().find(m => m.transcriptType === 'partial' && m.role === 'assistant');
                    return partial ? 'mouth-open' : '';
                  })()
                }
                style={{ display: 'block' }}
              >
                <circle cx="24" cy="24" r="22" fill="#FFEB3B" stroke="#FBC02D" strokeWidth="2" />
                <ellipse cx="16" cy="20" rx="3" ry="4" fill="#333" />
                <ellipse cx="32" cy="20" rx="3" ry="4" fill="#333" />
                {/* Smile or mouth-open */}
                <g>
                  <path
                    className="smile"
                    d="M16 30 Q24 38 32 30"
                    stroke="#333"
                    strokeWidth="2"
                    fill="none"
                    style={{ display: (() => {
                      const partial = [...transcript].reverse().find(m => m.transcriptType === 'partial' && m.role === 'assistant');
                      return partial ? 'none' : 'block';
                    })() }}
                  />
                  <ellipse
                    className="mouth-open"
                    cx="24" cy="33" rx="6" ry="4"
                    fill="#333"
                    style={{ display: (() => {
                      const partial = [...transcript].reverse().find(m => m.transcriptType === 'partial' && m.role === 'assistant');
                      return partial ? 'block' : 'none';
                    })() }}
                  />
                </g>
              </svg>
            </div>
            {/* Partial transcript as input */}
            <div className="flex-1 flex flex-col justify-center">
              <input
                type="text"
                className="w-full px-3 py-2 border-none text-gray-900 focus:outline-none "
                value={(() => {
                  // Find the latest partial transcript
                  const partial = [...transcript].reverse().find(m => m.transcriptType === 'partial');
                  return partial ? safeRender(partial.transcript) : '';
                })()}
                readOnly
                placeholder="Assistant is typing..."
                style={{background: '#fff'}}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal only shown for starting conversation */}
      {modalVisible && (
        <div className="fixed inset-0 flex items-center justify-center z-50 animate__animated animate__fadeInUp animate__faster">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md relative">
            <button className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-xl font-bold" onClick={handleClose} aria-label="Close">&times;</button>
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-yellow-400 text-blue-900 rounded-full w-10 h-10 flex items-center justify-center text-2xl font-bold">
                <span role="img" aria-label="connect">🔗</span>
              </div>
              <div className="text-left">
                <h2 className="text-xl font-bold text-blue-900">Let's Connect</h2>
                <p className="text-gray-600 text-sm">Tell us about yourself</p>
              </div>
            </div>
            {error && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded border border-red-300 text-sm">
                <strong>Error:</strong> {error}
              </div>
            )}
            <form className="mt-6 text-left" onSubmit={e => e.preventDefault()}>
              <label className="block font-semibold text-gray-700 mb-1" htmlFor="fullname">Full Name</label>
              <div className="flex gap-2 mb-4">
                <input id="firstname" name="firstname" type="text" placeholder="First name" value={form.firstname} onChange={handleFormChange} className="w-1/2 px-3 py-2 border rounded focus:outline-none focus:ring-2-400 bg-gray-50 text-gray-700" />
                <input id="lastname" name="lastname" type="text" placeholder="Last name" value={form.lastname} onChange={handleFormChange} className="w-1/2 px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-gray-50 text-gray-700" />
              </div>
              <label className="block font-semibold text-gray-700 mb-1" htmlFor="company">Company</label>
              <input id="company" name="company" type="text" placeholder="Enter your company name" value={form.company} onChange={handleFormChange} className="w-full px-3 py-2 border rounded mb-4 focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-gray-50 text-gray-700" />
              <label className="block font-semibold text-gray-700 mb-1" htmlFor="email">Email Address</label>
              <input id="email" name="email" type="email" placeholder="Enter your email address" value={form.email} onChange={handleFormChange} className="w-full px-3 py-2 border rounded mb-4 focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-gray-50 text-gray-700" />
              <label className="block font-semibold text-gray-700 mb-1" htmlFor="timezone">Timezone</label>
              <input
                id="timezone"
                name="timezone"
                type="text"
                list="timezone-list"
                value={form.timezone}
                onChange={handleFormChange}
                className="w-full px-3 py-2 border rounded mb-1 focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-gray-50 text-gray-700"
                placeholder="Search or select timezone"
                autoComplete="off"
              />
              <datalist id="timezone-list">
                <option value="Asia/Shanghai" />
                <option value="America/New_York" />
                <option value="Europe/London" />
                <option value="Asia/Tokyo" />
                <option value="Europe/Paris" />
                <option value="America/Los_Angeles" />
                <option value="Australia/Sydney" />
                <option value="Asia/Kolkata" />
                <option value="Europe/Berlin" />
                <option value="America/Chicago" />
                <option value="Africa/Johannesburg" />
                <option value="America/Sao_Paulo" />
                <option value="Pacific/Auckland" />
                <option value="Asia/Dubai" />
                <option value="Europe/Moscow" />
                <option value="Asia/Singapore" />
                <option value="Asia/Hong_Kong" />
                <option value="America/Toronto" />
                <option value="Europe/Madrid" />
                <option value="America/Vancouver" />
                <option value="Asia/Seoul" />
                <option value="Europe/Rome" />
                <option value="America/Mexico_City" />
                <option value="Asia/Bangkok" />
                <option value="Europe/Istanbul" />
                <option value="America/Denver" />
                <option value="America/Phoenix" />
                <option value="America/Anchorage" />
                <option value="America/Halifax" />
                <option value="America/Argentina/Buenos_Aires" />
                <option value="Africa/Cairo" />
                <option value="Asia/Jakarta" />
                <option value="Asia/Kuala_Lumpur" />
                <option value="Asia/Manila" />
                <option value="Asia/Riyadh" />
                <option value="Europe/Amsterdam" />
                <option value="Europe/Brussels" />
                <option value="Europe/Zurich" />
                <option value="Europe/Stockholm" />
                <option value="Europe/Warsaw" />
                <option value="Europe/Prague" />
                <option value="Europe/Budapest" />
                <option value="Europe/Athens" />
                <option value="Europe/Dublin" />
                <option value="Europe/Oslo" />
                <option value="Europe/Copenhagen" />
                <option value="Europe/Helsinki" />
                <option value="Europe/Lisbon" />
                <option value="Europe/Vienna" />
                <option value="Europe/Bratislava" />
                <option value="Europe/Bucharest" />
                <option value="Europe/Sofia" />
                <option value="Europe/Zagreb" />
                <option value="Europe/Belgrade" />
                <option value="Europe/Ljubljana" />
                <option value="Europe/Skopje" />
                <option value="Europe/Tallinn" />
                <option value="Europe/Riga" />
                <option value="Europe/Vilnius" />
                <option value="Europe/Kiev" />
                <option value="Europe/Minsk" />
                <option value="Europe/Sarajevo" />
                <option value="Europe/Podgorica" />
                <option value="Europe/Tirane" />
                <option value="Europe/Luxembourg" />
                <option value="Europe/Monaco" />
                <option value="Europe/San_Marino" />
                <option value="Europe/Vatican" />
              </datalist>
              <p className="text-xs text-gray-500 mb-4">Search or select your timezone</p>
              <button 
                type="button" 
                className="w-full px-4 py-2 bg-yellow-400 text-blue-900 font-semibold rounded shadow hover:bg-yellow-500 transition-colors duration-200 mb-2 disabled:opacity-50 disabled:cursor-not-allowed" 
                onClick={startCall}
                disabled={isCreatingContact}
              >
                {isCreatingContact ? 'Ringing...' : 'Start Voice Conversation'}
              </button>
              <p className="text-xs text-gray-500 text-center mt-2">Your information is secure and will only be used to personalize your conversation.</p>
            </form>
            {isConnected && (
              <div className="mt-4">
                <div className="mb-2 font-semibold">Transcript:</div>
                <div className="max-h-32 overflow-y-auto bg-gray-100 rounded p-2 text-left text-sm">
                  {transcript.map((msg, i) => (
                    <div key={i}>
                      {msg && typeof msg === 'object' ? safeRender(msg.transcript || msg) : safeRender(msg)}
                    </div>
                  ))}
                </div>
                <button className="mt-2 px-4 py-2 bg-red-400 text-white rounded" onClick={() => vapiInstance.current?.stop()}>
                  End Call
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Customer Appointment Form Modal */}
      {appointmentModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 animate__animated animate__fadeInUp animate__faster">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md relative">
            <button className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-xl font-bold" onClick={() => setAppointmentModal(false)} aria-label="Close">&times;</button>
            <h2 className="text-xl font-bold text-blue-900 mb-4">Customer Appointment Form</h2>
            <form onSubmit={handleAppointmentSubmit} className="space-y-5">
              {/* Hidden pre-filled fields for first name, last name, company, email, contact_id */}
              <input type="hidden" name="firstname" value={appointment.firstname} />
              <input type="hidden" name="lastname" value={appointment.lastname} />
              <input type="hidden" name="company" value={appointment.company} />
              <input type="hidden" name="email" value={appointment.email} />
              <input type="hidden" name="contact_id" value={contactId} />
              {/* Row 1: Time and Timezone */}
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="block font-semibold text-gray-700 mb-1" htmlFor="appointment-time">Time</label>
                  <select
                    id="appointment-time"
                    name="time"
                    value={appointment.time}
                    onChange={handleAppointmentChange}
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-gray-50 text-gray-700"
                    required
                  >
                    {(() => {
                      const times = [];
                      let hour = 9, minute = 0;
                      while (hour < 17 || (hour === 17 && minute === 0)) {
                        const value = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                        const ampm = hour < 12 ? 'AM' : 'PM';
                        const displayHour = hour % 12 === 0 ? 12 : hour % 12;
                        times.push(
                          <option key={value} value={value}>
                            {`${displayHour}:${minute.toString().padStart(2, '0')} ${ampm}`}
                          </option>
                        );
                        minute += 30;
                        if (minute === 60) {
                          minute = 0;
                          hour++;
                        }
                      }
                      return times;
                    })()}
                  </select>
                </div>
                <div className="flex-1">
                  <label className="block font-semibold text-gray-700 mb-1" htmlFor="appointment-timezone">Timezone</label>
                  <input
                    id="appointment-timezone"
                    name="timezone"
                    type="text"
                    list="timezone-list"
                    value={appointment.timezone}
                    onChange={handleAppointmentChange}
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-gray-50 text-gray-700"
                    placeholder="Search or select timezone"
                    autoComplete="off"
                    required
                  />
                  <datalist id="timezone-list">
                    <option value="Asia/Shanghai" />
                    <option value="America/New_York" />
                    <option value="Europe/London" />
                    <option value="Asia/Tokyo" />
                    <option value="Europe/Paris" />
                    <option value="America/Los_Angeles" />
                    <option value="Australia/Sydney" />
                    <option value="Asia/Kolkata" />
                    <option value="Europe/Berlin" />
                    <option value="America/Chicago" />
                    <option value="Africa/Johannesburg" />
                    <option value="America/Sao_Paulo" />
                    <option value="Pacific/Auckland" />
                    <option value="Asia/Dubai" />
                    <option value="Europe/Moscow" />
                    <option value="Asia/Singapore" />
                    <option value="Asia/Hong_Kong" />
                    <option value="America/Toronto" />
                    <option value="Europe/Madrid" />
                    <option value="America/Vancouver" />
                    <option value="Asia/Seoul" />
                    <option value="Europe/Rome" />
                    <option value="America/Mexico_City" />
                    <option value="Asia/Bangkok" />
                    <option value="Europe/Istanbul" />
                    <option value="America/Denver" />
                    <option value="America/Phoenix" />
                    <option value="America/Anchorage" />
                    <option value="America/Halifax" />
                    <option value="America/Argentina/Buenos_Aires" />
                    <option value="Africa/Cairo" />
                    <option value="Asia/Jakarta" />
                    <option value="Asia/Kuala_Lumpur" />
                    <option value="Asia/Manila" />
                    <option value="Asia/Riyadh" />
                    <option value="Europe/Amsterdam" />
                    <option value="Europe/Brussels" />
                    <option value="Europe/Zurich" />
                    <option value="Europe/Stockholm" />
                    <option value="Europe/Warsaw" />
                    <option value="Europe/Prague" />
                    <option value="Europe/Budapest" />
                    <option value="Europe/Athens" />
                    <option value="Europe/Dublin" />
                    <option value="Europe/Oslo" />
                    <option value="Europe/Copenhagen" />
                    <option value="Europe/Helsinki" />
                    <option value="Europe/Lisbon" />
                    <option value="Europe/Vienna" />
                    <option value="Europe/Bratislava" />
                    <option value="Europe/Bucharest" />
                    <option value="Europe/Sofia" />
                    <option value="Europe/Zagreb" />
                    <option value="Europe/Belgrade" />
                    <option value="Europe/Ljubljana" />
                    <option value="Europe/Skopje" />
                    <option value="Europe/Tallinn" />
                    <option value="Europe/Riga" />
                    <option value="Europe/Vilnius" />
                    <option value="Europe/Kiev" />
                    <option value="Europe/Minsk" />
                    <option value="Europe/Sarajevo" />
                    <option value="Europe/Podgorica" />
                    <option value="Europe/Tirane" />
                    <option value="Europe/Luxembourg" />
                    <option value="Europe/Monaco" />
                    <option value="Europe/San_Marino" />
                    <option value="Europe/Vatican" />
                  </datalist>
                </div>
              </div>
              {/* Row 2: Date Picker Calendar */}
              <div>
                <label className="block font-semibold text-gray-700 mb-1" htmlFor="appointment-date">Date</label>
                <CalendarTable
                  value={appointment.date}
                  onChange={date => handleAppointmentChange({ target: { name: 'date', value: date } })}
                />
              </div>
              {/* Row 3: Contact Number */}
              <div>
                <label className="block font-semibold text-gray-700 mb-1" htmlFor="appointment-areaCode">Contact Number</label>
                <div className="flex gap-2">
                  <select
                    id="appointment-areaCode"
                    name="areaCode"
                    value={appointment.areaCode}
                    onChange={handleAppointmentChange}
                    className="px-2 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-gray-50 text-gray-700"
                    style={{ minWidth: '90px' }}
                  >
                    <option value="+63">🇵🇭 +63 (Philippines)</option>
                    <option value="+1">🇺🇸 +1 (USA)</option>
                    <option value="+44">🇬🇧 +44 (UK)</option>
                    <option value="+61">🇦🇺 +61 (Australia)</option>
                    <option value="+81">🇯🇵 +81 (Japan)</option>
                    <option value="+86">🇨🇳 +86 (China)</option>
                    <option value="+91">🇮🇳 +91 (India)</option>
                    <option value="+49">🇩🇪 +49 (Germany)</option>
                    <option value="+33">🇫🇷 +33 (France)</option>
                    <option value="+39">🇮🇹 +39 (Italy)</option>
                  </select>
                  <input
                    id="appointment-number"
                    name="number"
                    type="tel"
                    value={appointment.number}
                    onChange={handleAppointmentChange}
                    className="flex-1 px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-gray-50 text-gray-700"
                    placeholder="Contact number"
                    required
                  />
                </div>
              </div>
              <button
                type="submit"
                className={`w-full px-4 py-2 font-semibold rounded shadow flex items-center justify-center gap-2 transition-colors duration-200 ${appointmentSuccess ? 'bg-green-500 text-white' : 'bg-yellow-400 text-blue-900 hover:bg-yellow-500'}`}
                disabled={appointmentLoading || appointmentSuccess}
              >
                {appointmentLoading && (
                  <svg className="animate-spin mr-2 h-5 w-5 text-blue-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                  </svg>
                )}
                {appointmentSuccess ? 'Success! Appointment submitted.' : appointmentLoading ? 'Submitting...' : 'Submit'}
              </button>
              {appointmentSuccess && (
                <div className="w-full text-center text-green-600 font-semibold mt-2">Your appointment was submitted successfully!</div>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function CalendarTable({ value, onChange }) {
  const [currentMonth, setCurrentMonth] = React.useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const today = new Date();
  today.setHours(0,0,0,0);
  // Ensure selectedDate is compared in local time (not UTC)
  // Use the value string directly for comparison
  const selectedDateStr = value;


  // Build calendar weeks, ensure correct date mapping
  const weeks = [];
  const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
  const startDay = firstDayOfMonth.getDay(); // 0=Sunday
  let dayCounter = 1 - startDay;
  for (let w = 0; w < 6; w++) {
    const week = [];
    for (let d = 0; d < 7; d++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), dayCounter);
      week.push(date);
      dayCounter++;
    }
    weeks.push(week);
  }
  function isWeekend(date) {
    return date.getDay() === 0 || date.getDay() === 6;
  }
  function isCurrentMonth(date) {
    return date.getMonth() === currentMonth.getMonth();
  }
  function isDisabled(date) {
    // Disable weekends, today, and any date before tomorrow
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    tomorrow.setHours(0,0,0,0);
    return (
      !isCurrentMonth(date) ||
      isWeekend(date) ||
      date < tomorrow
    );
  }

  return (
    <div className="bg-white rounded-lg p-4 shadow-lg">
      <div className="flex justify-between items-center mb-3">
        <button type="button" className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}>&lt;</button>
        <span className="font-semibold text-lg text-blue-900">{currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}</span>
        <button type="button" className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}>&gt;</button>
      </div>
      <table className="w-full text-center">
        <thead>
          <tr>
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((wd) => (
              <th key={wd} className="font-bold text-xs text-gray-600 py-1">{wd}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week, wi) => (
            <tr key={wi}>
              {week.map((date, di) => {
                const disabled = isDisabled(date);
                // Format date to yyyy-mm-dd for direct string comparison
                const dateStr = `${date.getFullYear()}-${(date.getMonth()+1).toString().padStart(2,'0')}-${date.getDate().toString().padStart(2,'0')}`;
                const selected = selectedDateStr === dateStr;
                return (
                  <td key={di} className="py-1">
                    <button
                      title={dateStr}
                      type="button"
                      disabled={disabled}
                      onClick={() => !disabled && onChange(dateStr)}
                      className={
                        `w-8 h-8 rounded-full transition ` +
                        (selected ? 'bg-yellow-400 text-blue-900 font-bold shadow' : disabled ? 'bg-gray-100 text-gray-400' : 'bg-white hover:bg-yellow-100 text-gray-700 hover:scale-105')
                      }
                      style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
                    >
                      {date.getMonth() === currentMonth.getMonth() ? date.getDate() : ''}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="text-xs text-gray-500 mt-2 text-center">* Only future weekdays are selectable</div>
    </div>
  );
}

export default App;
